import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMultiKillStats, MultiKillResponse } from "../../services/RatingAnalysisService";
import Loading from "../../components/Loading";
import PlayerImage from "../../components/PlayerImage";
import { getLatestSeason, getDivisionsBySeason } from "../../services/MatchService";
import { Whatshot, Stars, EmojiEvents } from '@mui/icons-material';
import styles from '../../styles/StatsView.module.css';

const StatsViewMultiPage = () => {
    const [loading, setLoading] = useState(true);
    const [topData, setTopData] = useState<MultiKillResponse | null>(null);
    const [bottomData, setBottomData] = useState<MultiKillResponse | null>(null);
    const [season, setSeason] = useState<number>(29);
    const [division, setDivision] = useState<string>("all");
    const [displayCount, setDisplayCount] = useState<number>(10);
    const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
    const [availableDivisions, setAvailableDivisions] = useState<string[]>([]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const latestSeason = await getLatestSeason();
                setSeason(latestSeason);
                
                const seasons = [];
                for (let s = 26; s <= latestSeason; s++) {
                    seasons.push(s);
                }
                setAvailableSeasons(seasons.reverse());

                const divisions = await getDivisionsBySeason(latestSeason);
                
                // Add aggregate division options for initial load
                const aggregateOptions: string[] = [];
                
                // Check if we have any 2div subdivisions
                if (divisions.some((d: string) => d.startsWith('2div'))) {
                    aggregateOptions.push('2div');
                }
                
                // Check if we have any 3div subdivisions
                if (divisions.some((d: string) => d.startsWith('3div'))) {
                    aggregateOptions.push('3div');
                }
                
                // Check if we have any 4div subdivisions
                if (divisions.some((d: string) => d.startsWith('4div'))) {
                    aggregateOptions.push('4div');
                }
                
                const divisionList = ["all", ...aggregateOptions, ...divisions];
                setAvailableDivisions(divisionList);
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const [topResponse, bottomResponse] = await Promise.all([
                    fetchMultiKillStats(season, division, true),
                    fetchMultiKillStats(season, division, false)
                ]);
                setTopData(topResponse);
                setBottomData(bottomResponse);
            } catch (error) {
                console.error("Error loading multi-kill data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (season && division) {
            loadData();
        }
    }, [season, division]);

    useEffect(() => {
        const fetchDivisionsForSeason = async () => {
            try {
                const divisions = await getDivisionsBySeason(season);
                
                // Add aggregate division options
                const aggregateOptions: string[] = [];
                
                // Check if we have any 2div subdivisions
                if (divisions.some((d: string) => d.startsWith('2div'))) {
                    aggregateOptions.push('2div');
                }
                
                // Check if we have any 3div subdivisions
                if (divisions.some((d: string) => d.startsWith('3div'))) {
                    aggregateOptions.push('3div');
                }
                
                // Check if we have any 4div subdivisions
                if (divisions.some((d: string) => d.startsWith('4div'))) {
                    aggregateOptions.push('4div');
                }
                
                const divisionList = ["all", ...aggregateOptions, ...divisions];
                setAvailableDivisions(divisionList);
            } catch (error) {
                console.error("Error fetching divisions:", error);
            }
        };

        if (season) {
            fetchDivisionsForSeason();
        }
    }, [season]);

    const formatDivision = (div: string) => {
        if (div === "all") return "Alle Divisioner";
        const divMap: { [key: string]: string } = {
            'ligaen': 'Ligaen',
            '1div': '1. Division',
            '2div': '2. Division (Alle)',
            '3div': '3. Division (Alle)',
            '4div': '4. Division (Alle)',
            '2divA': '2. Division A',
            '2divB': '2. Division B',
            '3divA': '3. Division A',
            '3divB': '3. Division B',
            '3divC': '3. Division C',
            '3divD': '3. Division D',
            '4divA': '4. Division A',
            '4divB': '4. Division B',
            '4divC': '4. Division C',
            '4divD': '4. Division D',
            '4divE': '4. Division E',
            '4divF': '4. Division F'
        };
        return divMap[div] || div;
    };

    const getRankClass = (index: number) => {
        switch(index) {
            case 0: return styles.rank1;
            case 1: return styles.rank2;
            case 2: return styles.rank3;
            default: return styles.rankOther;
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (!topData || !bottomData) {
        return <div className="text-center mt-5 text-white">No data available</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.twitterCard}>
                {/* Compact Header */}
                <div className={styles.compactHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.titleSection}>
                            <h1 className={styles.mainTitle}>Multi-Kill Mestre</h1>
                            <div className={styles.subtitle}>{formatDivision(division)}</div>
                        </div>
                        <div className={styles.seasonBadge}>Sæson {season}</div>
                    </div>
                </div>

                {/* Split Layout */}
                <div className={styles.splitContainer}>
                    {/* Most Multi-Kills Column */}
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <div className={`${styles.columnIcon} ${styles.gainIcon}`}>
                                <Whatshot style={{ color: '#28a745' }} />
                            </div>
                            <div className={styles.columnTitle}>
                                <h3>Flest Multi-Kills</h3>
                                <div className={styles.columnSubtitle}>Højeste multi-kill score</div>
                            </div>
                        </div>
                        <div className={styles.playerList}>
                            {topData.multiKillers.slice(0, displayCount).map((player, index) => (
                                <div key={player.steamId} className={`${styles.playerCard} ${styles.gainCard}`}>
                                    <div className={`${styles.rankBadge} ${getRankClass(index)}`}>
                                        {index + 1}
                                    </div>
                                    <div className={styles.playerImage}>
                                        <PlayerImage 
                                            id={player.steamId} 
                                            width={50}
                                        />
                                    </div>
                                    <div className={styles.playerInfo}>
                                        <Link to={`/player/${player.steamId}`} className={styles.playerName}>
                                            {player.playerName}
                                        </Link>
                                        <div className={styles.teamInfo}>
                                            <span>{player.teamName}</span>
                                            <span className={styles.divisionBadge}>{formatDivision(player.division)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.ratingStats}>
                                        <div className={`${styles.changeValue} ${styles.gainValue}`}>
                                            {player.multiKillScore}
                                        </div>
                                        <div className={styles.ratingValues}>
                                            {player.fiveKillRounds > 0 && (
                                                <span style={{color: '#ffd700'}}>
                                                    <Stars fontSize="small" style={{verticalAlign: 'middle'}} /> {player.fiveKillRounds} ACE{player.fiveKillRounds > 1 ? 'S' : ''}
                                                </span>
                                            )}
                                            {player.fiveKillRounds === 0 && (
                                                <span>{player.fourKillRounds} 4K • {player.threeKillRounds} 3K</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Ace Leaders Column */}
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <div className={`${styles.columnIcon} ${styles.gainIcon}`} style={{background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.3))'}}>
                                <Stars style={{ color: '#ffd700' }} />
                            </div>
                            <div className={styles.columnTitle}>
                                <h3>Ace Ledere</h3>
                                <div className={styles.columnSubtitle}>Flest aces i sæsonen</div>
                            </div>
                        </div>
                        <div className={styles.playerList}>
                            {topData.aceLeaders.slice(0, displayCount).map((player, index) => (
                                <div key={player.steamId} className={`${styles.playerCard} ${styles.gainCard}`}>
                                    <div className={`${styles.rankBadge} ${getRankClass(index)}`}>
                                        {index + 1}
                                    </div>
                                    <div className={styles.playerImage}>
                                        <PlayerImage 
                                            id={player.steamId} 
                                            width={50}
                                        />
                                    </div>
                                    <div className={styles.playerInfo}>
                                        <Link to={`/player/${player.steamId}`} className={styles.playerName}>
                                            {player.playerName}
                                        </Link>
                                        <div className={styles.teamInfo}>
                                            <span>{player.teamName}</span>
                                            <span className={styles.divisionBadge}>{formatDivision(player.division)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.ratingStats}>
                                        <div className={`${styles.changeValue}`} style={{color: '#ffd700'}}>
                                            <Stars fontSize="small" style={{verticalAlign: 'middle', marginRight: '4px'}} />
                                            {player.aces}
                                        </div>
                                        <div className={styles.ratingValues}>
                                            {player.acesPerMatch.toFixed(2)} per kamp • {player.totalMatches} kampe
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Info Box */}
                <div className={styles.infoBox}>
                    <strong>Multi-Kills:</strong> Multi-kill score beregnes: 2K = 2 point, 3K = 3 point, 4K = 4 point, ACE = 5 point. 
                    Spillere med mange multi-kills har høj impact og kan vende runder.
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <div className={styles.footerText}>
                        <span className={styles.highlight}>PowerStats.dk</span> • Avanceret CS2 Statistik • Sæson {season}
                    </div>
                </div>

                {/* Filter Controls - Hidden at bottom */}
                <div className={styles.filterControls}>
                    <div className={styles.filterBar}>
                        <select 
                            className={styles.filterSelect}
                            value={season}
                            onChange={(e) => setSeason(Number(e.target.value))}
                        >
                            {availableSeasons.map(s => (
                                <option key={s} value={s}>Sæson {s}</option>
                            ))}
                        </select>
                        <select 
                            className={styles.filterSelect}
                            value={division}
                            onChange={(e) => setDivision(e.target.value)}
                        >
                            {availableDivisions.map(div => (
                                <option key={div} value={div}>
                                    {formatDivision(div)}
                                </option>
                            ))}
                        </select>
                        <select 
                            className={styles.filterSelect}
                            value={displayCount}
                            onChange={(e) => setDisplayCount(Number(e.target.value))}
                        >
                            <option value={5}>Vis Top 5</option>
                            <option value={10}>Vis Top 10</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatsViewMultiPage;